import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import api, { formatApiError, assetUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Field, TextInput, TextArea, ImageUpload, StringList, Card } from "@/components/admin/Fields";
import { toast } from "sonner";
import {
  Save, LogOut, ExternalLink, Loader2, Plus, RotateCcw,
  Image, LayoutGrid, Eye, Info, BookMarked, MessageSquareQuote, MapPin, Share2, UserCog,
  Tag, Images, ClipboardList,
} from "lucide-react";

const TABS = [
  { key: "brand", label: "Brand", icon: Image },
  { key: "hero", label: "Hero / Banner", icon: LayoutGrid },
  { key: "stats", label: "Statistik", icon: LayoutGrid },
  { key: "visimisi", label: "Visi & Misi", icon: Eye },
  { key: "about", label: "Tentang Kami", icon: Info },
  { key: "programs", label: "Program", icon: BookMarked },
  { key: "pricing", label: "Harga", icon: Tag },
  { key: "testimonials", label: "Testimoni", icon: MessageSquareQuote },
  { key: "gallery", label: "Galeri", icon: Images },
  { key: "registration", label: "Pendaftaran", icon: ClipboardList },
  { key: "location", label: "Lokasi", icon: MapPin },
  { key: "contact", label: "Kontak", icon: Share2 },
  { key: "account", label: "Akun Admin", icon: UserCog },
];

const ICON_OPTIONS = ["BookOpen", "GraduationCap", "Trophy", "Target"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { content, refetch } = useContent();
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("brand");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) setDraft(JSON.parse(JSON.stringify(content)));
  }, [content]);

  if (!draft) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-10 h-10 text-sky-500 animate-spin" />
      </div>
    );
  }

  const upd = (section, key, value) =>
    setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: value } }));
  const setArr = (section, value) => setDraft((d) => ({ ...d, [section]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", draft);
      await refetch();
      toast.success("Perubahan berhasil disimpan & tampil di website");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const resetDefault = async () => {
    if (!window.confirm("Kembalikan semua konten ke bawaan? Perubahan Anda akan hilang.")) return;
    try {
      const { data } = await api.post("/content/reset");
      setDraft(JSON.parse(JSON.stringify(data)));
      await refetch();
      toast.success("Konten dikembalikan ke default");
    } catch {
      toast.error("Gagal mereset konten");
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Logo size={38} logoUrl={assetUrl(draft.brand.logo)} />
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition"
            >
              <ExternalLink className="w-4 h-4" /> Lihat Website
            </a>
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-content-button"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold shadow-lg shadow-sky-500/30 transition disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Simpan
            </button>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-500 transition"
              aria-label="Keluar"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[240px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 h-max">
          <div className="bg-white rounded-2xl border border-slate-200 p-2 flex lg:flex-col gap-1 overflow-x-auto">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`admin-tab-${t.key}`}
                  className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition ${
                    tab === t.key ? "bg-sky-500 text-white shadow" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
            <button
              onClick={resetDefault}
              className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-amber-600 hover:bg-amber-50 transition whitespace-nowrap mt-2"
            >
              <RotateCcw className="w-4 h-4" /> Reset Default
            </button>
          </div>
          <p className="hidden lg:block text-xs text-slate-400 mt-3 px-2">
            Masuk sebagai<br /><span className="font-semibold text-slate-600">{user?.email}</span>
          </p>
        </aside>

        {/* Panel */}
        <main className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 min-h-[70vh]">
          {tab === "brand" && (
            <Section title="Brand & Identitas">
              <Field label="Nama Brand"><TextInput value={draft.brand.name} onChange={(v) => upd("brand", "name", v)} /></Field>
              <Field label="Tagline"><TextInput value={draft.brand.tagline} onChange={(v) => upd("brand", "tagline", v)} /></Field>
              <Field label="Icon Website (favicon)" hint="Tampil di tab browser. Disarankan gambar persegi (PNG/SVG, mis. 64x64).">
                <ImageUpload value={draft.brand.favicon} onChange={(v) => upd("brand", "favicon", v)} testid="favicon-upload" />
              </Field>
              <Field label="Icon / Logo Navbar" hint="Tampil di header & footer. Disarankan gambar persegi (PNG/SVG transparan).">
                <ImageUpload value={draft.brand.logo} onChange={(v) => upd("brand", "logo", v)} testid="logo-upload" />
              </Field>
              <Field label="Link LMS / Pendaftaran Online" hint="Tujuan tombol 'Daftar Sekarang' di banner & section pendaftaran.">
                <TextInput value={draft.brand.lmsUrl} onChange={(v) => upd("brand", "lmsUrl", v)} testid="lms-url-input" />
              </Field>
            </Section>
          )}

          {tab === "hero" && (
            <Section title="Hero / Banner Selamat Datang">
              <Field label="Teks Badge"><TextInput value={draft.hero.badge} onChange={(v) => upd("hero", "badge", v)} /></Field>
              <Field label="Judul Awal"><TextInput value={draft.hero.titleLead} onChange={(v) => upd("hero", "titleLead", v)} /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Judul Sorot 1"><TextInput value={draft.hero.titleHighlight1} onChange={(v) => upd("hero", "titleHighlight1", v)} /></Field>
                <Field label="Judul Sorot 2"><TextInput value={draft.hero.titleHighlight2} onChange={(v) => upd("hero", "titleHighlight2", v)} /></Field>
              </div>
              <Field label="Subjudul"><TextArea value={draft.hero.subtitle} onChange={(v) => upd("hero", "subtitle", v)} /></Field>
              <Field label="Teks Tombol CTA"><TextInput value={draft.hero.ctaText} onChange={(v) => upd("hero", "ctaText", v)} /></Field>
              <Field label="Teks Rating"><TextInput value={draft.hero.ratingText} onChange={(v) => upd("hero", "ratingText", v)} /></Field>
              <Field label="Gambar Latar Banner"><ImageUpload value={draft.hero.bgImage} onChange={(v) => upd("hero", "bgImage", v)} testid="hero-image-upload" /></Field>
            </Section>
          )}

          {tab === "stats" && (
            <Section title="Statistik (angka di banner)">
              <div className="grid sm:grid-cols-2 gap-4">
                {draft.stats.map((s, i) => (
                  <Card key={i} title={`Statistik ${i + 1}`} onRemove={() => setArr("stats", draft.stats.filter((_, idx) => idx !== i))}>
                    <Field label="Nilai"><TextInput value={s.value} onChange={(v) => { const n = [...draft.stats]; n[i] = { ...n[i], value: v }; setArr("stats", n); }} /></Field>
                    <Field label="Label"><TextInput value={s.label} onChange={(v) => { const n = [...draft.stats]; n[i] = { ...n[i], label: v }; setArr("stats", n); }} /></Field>
                  </Card>
                ))}
              </div>
              <AddButton onClick={() => setArr("stats", [...draft.stats, { value: "", label: "" }])} label="Tambah Statistik" />
            </Section>
          )}

          {tab === "visimisi" && (
            <Section title="Visi & Misi">
              <Field label="Visi"><TextArea value={draft.visi} onChange={(v) => setDraft((d) => ({ ...d, visi: v }))} rows={4} /></Field>
              <Field label="Misi (poin per baris)"><StringList items={draft.misi} onChange={(v) => setDraft((d) => ({ ...d, misi: v }))} placeholder="Tambah Misi" testidPrefix="misi-item" /></Field>
            </Section>
          )}

          {tab === "about" && (
            <Section title="Tentang Kami">
              <Field label="Judul"><TextInput value={draft.about.title} onChange={(v) => upd("about", "title", v)} /></Field>
              <Field label="Deskripsi"><TextArea value={draft.about.body} onChange={(v) => upd("about", "body", v)} rows={5} /></Field>
              <Field label="Badge Tahun"><TextInput value={draft.about.yearsBadge} onChange={(v) => upd("about", "yearsBadge", v)} /></Field>
              <Field label="Keunggulan"><StringList items={draft.about.features} onChange={(v) => upd("about", "features", v)} placeholder="Tambah Keunggulan" /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Gambar Utama"><ImageUpload value={draft.about.image1} onChange={(v) => upd("about", "image1", v)} /></Field>
                <Field label="Gambar Kedua"><ImageUpload value={draft.about.image2} onChange={(v) => upd("about", "image2", v)} /></Field>
              </div>
            </Section>
          )}

          {tab === "programs" && (
            <Section title="Program Unggulan">
              <div className="grid sm:grid-cols-2 gap-4">
                {draft.programs.map((p, i) => (
                  <Card key={i} title={`Program ${i + 1}`} onRemove={() => setArr("programs", draft.programs.filter((_, idx) => idx !== i))}>
                    <Field label="Judul"><TextInput value={p.title} onChange={(v) => { const n = [...draft.programs]; n[i] = { ...n[i], title: v }; setArr("programs", n); }} /></Field>
                    <Field label="Deskripsi"><TextArea value={p.desc} onChange={(v) => { const n = [...draft.programs]; n[i] = { ...n[i], desc: v }; setArr("programs", n); }} rows={3} /></Field>
                    <Field label="Ikon">
                      <select value={p.icon} onChange={(v) => { const n = [...draft.programs]; n[i] = { ...n[i], icon: v.target.value }; setArr("programs", n); }} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400">
                        {ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                  </Card>
                ))}
              </div>
              <AddButton onClick={() => setArr("programs", [...draft.programs, { title: "", desc: "", icon: "BookOpen" }])} label="Tambah Program" />
            </Section>
          )}

          {tab === "testimonials" && (
            <Section title="Testimoni">
              <div className="grid sm:grid-cols-2 gap-4">
                {draft.testimonials.map((t, i) => (
                  <Card key={i} title={`Testimoni ${i + 1}`} onRemove={() => setArr("testimonials", draft.testimonials.filter((_, idx) => idx !== i))}>
                    <Field label="Nama"><TextInput value={t.name} onChange={(v) => { const n = [...draft.testimonials]; n[i] = { ...n[i], name: v }; setArr("testimonials", n); }} /></Field>
                    <Field label="Peran"><TextInput value={t.role} onChange={(v) => { const n = [...draft.testimonials]; n[i] = { ...n[i], role: v }; setArr("testimonials", n); }} /></Field>
                    <Field label="Testimoni"><TextArea value={t.text} onChange={(v) => { const n = [...draft.testimonials]; n[i] = { ...n[i], text: v }; setArr("testimonials", n); }} rows={3} /></Field>
                    <Field label="Foto"><ImageUpload value={t.image} onChange={(v) => { const n = [...draft.testimonials]; n[i] = { ...n[i], image: v }; setArr("testimonials", n); }} /></Field>
                  </Card>
                ))}
              </div>
              <AddButton onClick={() => setArr("testimonials", [...draft.testimonials, { name: "", role: "", text: "", image: "" }])} label="Tambah Testimoni" />
            </Section>
          )}

          {tab === "location" && (
            <Section title="Lokasi & Google Maps">
              <Field label="Alamat"><TextArea value={draft.location.address} onChange={(v) => upd("location", "address", v)} rows={2} /></Field>
              <Field label="Jam Operasional"><TextInput value={draft.location.hours} onChange={(v) => upd("location", "hours", v)} /></Field>
              <Field label="URL Embed Google Maps" hint="Gunakan link dengan &output=embed">
                <TextInput value={draft.location.mapsEmbed} onChange={(v) => upd("location", "mapsEmbed", v)} />
              </Field>
              <Field label="Link Google Maps (tombol arah)"><TextInput value={draft.location.mapsLink} onChange={(v) => upd("location", "mapsLink", v)} /></Field>
            </Section>
          )}

          {tab === "contact" && (
            <Section title="Kontak & Media Sosial">
              <Field label="Nomor WhatsApp" hint="Format internasional tanpa +, contoh: 6281337653746">
                <TextInput value={draft.contact.whatsapp} onChange={(v) => upd("contact", "whatsapp", v)} />
              </Field>
              <Field label="Link Instagram"><TextInput value={draft.contact.instagram} onChange={(v) => upd("contact", "instagram", v)} /></Field>
              <Field label="Link TikTok"><TextInput value={draft.contact.tiktok} onChange={(v) => upd("contact", "tiktok", v)} /></Field>
              <Field label="Link Facebook"><TextInput value={draft.contact.facebook} onChange={(v) => upd("contact", "facebook", v)} /></Field>
            </Section>
          )}

          {tab === "registration" && (
            <Section title="Section Pendaftaran">
              <Field label="Judul"><TextInput value={draft.registration?.title} onChange={(v) => upd("registration", "title", v)} /></Field>
              <Field label="Subjudul"><TextArea value={draft.registration?.subtitle} onChange={(v) => upd("registration", "subtitle", v)} rows={2} /></Field>
              <Field label="Pilihan Jenjang" hint="Opsi yang muncul pada dropdown jenjang di formulir pendaftaran.">
                <StringList
                  items={draft.registration?.levels || ["SD", "SMP", "SMA", "Persiapan UTBK", "Lainnya"]}
                  onChange={(v) => upd("registration", "levels", v)}
                  placeholder="Tambah Jenjang"
                  testidPrefix="level-item"
                />
              </Field>
              <p className="text-sm text-slate-500">
                Formulir pendaftaran mengirim data (nama, jenjang, no. HP, pesan) langsung ke WhatsApp admin
                (nomor diatur di tab <b>Kontak</b>). Tombol "Daftar Sekarang" menuju Link LMS (diatur di tab <b>Brand</b>).
              </p>
            </Section>
          )}

          {tab === "pricing" && (
            <Section title="Paket & Harga">
              <Field label="Judul Section"><TextInput value={draft.pricing?.title} onChange={(v) => upd("pricing", "title", v)} /></Field>
              <Field label="Subjudul"><TextArea value={draft.pricing?.subtitle} onChange={(v) => upd("pricing", "subtitle", v)} rows={2} /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                {(draft.pricing?.packages || []).map((pkg, i) => {
                  const setPkgs = (arr) => setDraft((d) => ({ ...d, pricing: { ...d.pricing, packages: arr } }));
                  const patch = (patchObj) => { const n = [...draft.pricing.packages]; n[i] = { ...n[i], ...patchObj }; setPkgs(n); };
                  return (
                    <Card key={i} title={`Paket ${i + 1}`} onRemove={() => setPkgs(draft.pricing.packages.filter((_, idx) => idx !== i))}>
                      <Field label="Nama Paket"><TextInput value={pkg.name} onChange={(v) => patch({ name: v })} /></Field>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Harga (Rp)"><TextInput value={pkg.price} onChange={(v) => patch({ price: v })} /></Field>
                        <Field label="Periode"><TextInput value={pkg.period} onChange={(v) => patch({ period: v })} /></Field>
                      </div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={!!pkg.popular} onChange={(e) => patch({ popular: e.target.checked })} className="w-4 h-4 accent-sky-500" />
                        Tandai sebagai "Paling Populer"
                      </label>
                      <Field label="Fitur"><StringList items={pkg.features || []} onChange={(v) => patch({ features: v })} placeholder="Tambah Fitur" /></Field>
                    </Card>
                  );
                })}
              </div>
              <AddButton onClick={() => setDraft((d) => ({ ...d, pricing: { ...d.pricing, packages: [...(d.pricing?.packages || []), { name: "", price: "", period: "/bulan", features: [], popular: false }] } }))} label="Tambah Paket" />
            </Section>
          )}

          {tab === "gallery" && (
            <Section title="Galeri Kegiatan">
              <Field label="Judul Section"><TextInput value={draft.gallery?.title} onChange={(v) => upd("gallery", "title", v)} /></Field>
              <Field label="Subjudul"><TextArea value={draft.gallery?.subtitle} onChange={(v) => upd("gallery", "subtitle", v)} rows={2} /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                {(draft.gallery?.images || []).map((img, i) => {
                  const setImgs = (arr) => setDraft((d) => ({ ...d, gallery: { ...d.gallery, images: arr } }));
                  const patch = (patchObj) => { const n = [...draft.gallery.images]; n[i] = { ...n[i], ...patchObj }; setImgs(n); };
                  return (
                    <Card key={i} title={`Foto ${i + 1}`} onRemove={() => setImgs(draft.gallery.images.filter((_, idx) => idx !== i))}>
                      <Field label="Gambar"><ImageUpload value={img.image} onChange={(v) => patch({ image: v })} /></Field>
                      <Field label="Keterangan"><TextInput value={img.caption} onChange={(v) => patch({ caption: v })} /></Field>
                    </Card>
                  );
                })}
              </div>
              <AddButton onClick={() => setDraft((d) => ({ ...d, gallery: { ...d.gallery, images: [...(d.gallery?.images || []), { image: "", caption: "" }] } }))} label="Tambah Foto" />
            </Section>
          )}

          {tab === "account" && <AccountPanel user={user} />}
        </main>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="space-y-5">
    <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h2>
    <div className="space-y-5">{children}</div>
  </div>
);

const AddButton = ({ onClick, label }) => (
  <button onClick={onClick} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-sky-300 text-sky-600 text-sm font-semibold hover:bg-sky-50 transition">
    <Plus className="w-4 h-4" /> {label}
  </button>
);

function AccountPanel({ user }) {
  const { setUser } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(false);

  const isGoogle = false; // allow attempt; backend validates
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {};
      if (email && email !== user?.email) payload.email = email;
      if (next) { payload.new_password = next; payload.current_password = current; }
      if (Object.keys(payload).length === 0) { toast.info("Tidak ada perubahan"); setLoading(false); return; }
      const { data } = await api.put("/auth/account", payload);
      if (data.token) localStorage.setItem("adhi_token", data.token);
      setUser(data.user);
      setCurrent(""); setNext("");
      toast.success("Akun berhasil diperbarui");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal memperbarui akun");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section title="Pengaturan Akun Admin">
      <p className="text-sm text-slate-500 -mt-2">
        Ubah email & password untuk login email/password. (Login Google tidak memerlukan password.)
      </p>
      <form onSubmit={submit} className="space-y-4 max-w-md">
        <Field label="Email"><TextInput value={email} onChange={setEmail} /></Field>
        <Field label="Password Saat Ini" hint="Wajib diisi jika mengubah password">
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
        </Field>
        <Field label="Password Baru">
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
        </Field>
        <button type="submit" disabled={loading} data-testid="update-account-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-bold shadow-lg shadow-sky-500/30 transition disabled:opacity-60">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Perbarui Akun
        </button>
      </form>
    </Section>
  );
}
